import { useCreateDataPoint } from '../DataPoint';
import { UpdateNoteReceivableValueServiceInput, UpdateNoteReceivableServiceInput } from './types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCreateFileAttachment } from '../FileAttachment';
import { CreateNoteReceivableServiceInput } from './types';
import { useCreateNoteReceivableMutation } from '../generated/graphql';
import { useGraphqlClient } from '../graphql';
import { noteReceivableKeys } from './queryKeys';
import { useUpdateNoteReceivable } from './mutations';

export const useUpdateNoteReceivableValueService = () => {
  const { createDataPoint } = useCreateDataPoint();
  const queryClient = useQueryClient();

  const updateNoteReceivableValueService = useMutation({
    mutationKey: ['updateNoteReceivableValueService'],
    mutationFn: async (input: UpdateNoteReceivableValueServiceInput) => {
      await createDataPoint.mutateAsync({
        dataPoint: {
          dateTime: new Date().toISOString(),
          data: {
            monetaryAmountValue: {
              value: {
                currencyCode: 'USD',
                value: input.valueInCents,
              },
            },
          },
          groupID: input.valueDataPointGroupID,
        },
        tenantID: input.householdID,
      });
    },
    onSuccess: (_, { householdID, noteReceivableID }) => {
      queryClient.invalidateQueries(noteReceivableKeys.noteReceivable({ householdID, id: noteReceivableID }));
    },
  });

  return { updateNoteReceivableValueService };
};

export function useCreateNoteReceivableService() {
  const { createFileAttachment } = useCreateFileAttachment();
  const createNoteReceivable = useCreateNoteReceivableMutation(useGraphqlClient());
  const { updateNoteReceivableValueService } = useUpdateNoteReceivableValueService();

  const createNoteReceivableService = useMutation({
    mutationFn: async ({
      files,
      createNoteReceivableMutationInput,
      valueInCents,
    }: CreateNoteReceivableServiceInput) => {
      const {
        createNoteReceivable: { noteReceivable },
      } = await createNoteReceivable.mutateAsync({ input: createNoteReceivableMutationInput });

      if (valueInCents) {
        updateNoteReceivableValueService.mutate({
          valueInCents,
          noteReceivableID: noteReceivable.id,
          householdID: noteReceivable.householdID,
          valueDataPointGroupID: noteReceivable.value.id,
        });
      }

      files?.forEach((file) => {
        createFileAttachment.mutate({
          tenantID: noteReceivable.householdID,
          fileAttachment: { itemID: noteReceivable.id, fileID: file.id },
        });
      });

      return noteReceivable;
    },
  });

  return { createNoteReceivableService };
}

/**
 * @returns a function that:
 * 1. Updates a note receivable
 * 2. Creates a data point for the note receivable value data point group
 * 3. Creates file attachments for any pending files
 */
export const useUpdateNoteReceivableService = () => {
  const { updateNoteReceivableValueService } = useUpdateNoteReceivableValueService();
  const { updateNoteReceivable } = useUpdateNoteReceivable();
  const { createFileAttachment } = useCreateFileAttachment();
  const queryClient = useQueryClient();

  const updateNoteReceivableService = useMutation({
    mutationKey: ['updateNoteReceivableService'],
    mutationFn: async (input: UpdateNoteReceivableServiceInput) => {
      const { noteReceivable } = await updateNoteReceivable.mutateAsync(
        input.updateNoteReceivableInput,
        input.updateNoteReceivableMutationOptions
      );

      await updateNoteReceivableValueService.mutateAsync({
        householdID: noteReceivable.householdID,
        valueDataPointGroupID: noteReceivable.value.id,
        valueInCents: input.valueInCents,
        noteReceivableID: noteReceivable.id,
      });

      await Promise.all(
        (input.files || []).map((pendingFile) =>
          createFileAttachment.mutateAsync({
            tenantID: noteReceivable.householdID,
            fileAttachment: { fileID: pendingFile.id, itemID: noteReceivable.id },
          })
        )
      );
    },
    onSuccess: (_, { updateNoteReceivableInput: { householdID, id } }) => {
      queryClient.invalidateQueries(noteReceivableKeys.noteReceivable({ householdID, id }));
    },
  });

  return { updateNoteReceivableService };
};
